import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import { getNodes } from '@/utils/graphqlUtils';

import {
  HypotheticalCharitableTrustFormShape,
  HypotheticalCharitableTrustModalKind,
} from '../EstateWaterfallHypotheticalCharitableTrustModal.types';
import { useEstateWaterfallHypotheticalCharitableTrustModalQuery } from '../graphql/EstateWaterfallHypotheticalCharitableTrustModal.generated';

interface UseSelectedProposalDataProps {
  charitableTrustModalKind: HypotheticalCharitableTrustModalKind;
  householdId: string;
  waterfallId: string;
}

export function useSelectedProposalData({
  charitableTrustModalKind,
  householdId,
  waterfallId,
}: UseSelectedProposalDataProps) {
  const { control } = useFormContext<HypotheticalCharitableTrustFormShape>();

  const selectedProposalId = useWatch({
    control,
    name: 'simulatedProposalId',
  });

  // Get the data from the cache if possible
  const { data } = useEstateWaterfallHypotheticalCharitableTrustModalQuery({
    variables: {
      waterfallId,
      householdId,
      isCrt:
        charitableTrustModalKind === HypotheticalCharitableTrustModalKind.CRT,
      isClt:
        charitableTrustModalKind === HypotheticalCharitableTrustModalKind.CLT,
    },
    fetchPolicy: 'cache-first', // Should be in cache already because this is the same query that is used in the modal
  });

  // Extract the asset value from the proposal
  const proposalAssetValue = useMemo(() => {
    if (!selectedProposalId || !data) return null;

    if (
      charitableTrustModalKind === HypotheticalCharitableTrustModalKind.CRT &&
      data.crtOptions
    ) {
      const proposals = getNodes(data.crtOptions);
      const proposal = proposals.find((p) => p && p.id === selectedProposalId);

      if (proposal?.crtProposal) {
        return proposal.crtProposal.assetValue;
      }
    } else if (
      charitableTrustModalKind === HypotheticalCharitableTrustModalKind.CLT &&
      data.cltOptions
    ) {
      const proposals = getNodes(data.cltOptions);
      const proposal = proposals.find((p) => p && p.id === selectedProposalId);

      if (proposal?.cltProposal) {
        return proposal.cltProposal.assetValue;
      }
    }

    return null;
  }, [charitableTrustModalKind, data, selectedProposalId]);

  return {
    proposalAssetValue,
    selectedProposalId,
  };
}
